import React, { useCallback, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import jsQR from 'jsqr';

import { Colors } from './theme';

/**
 * The camera, reading a job card sticker.
 *
 * The phone build hands this to `expo-camera`, which is a native barcode reader.
 * A browser has one only in Chrome (`BarcodeDetector`) — and the supervisor this
 * page exists for is on iOS, where Safari has none. So the frames are decoded in
 * JS instead, and `BarcodeDetector` is used where it exists because it is the
 * cheaper of the two on a handset that has been on the floor all day.
 *
 * `facingMode: environment` picks the back camera. Getting at it at all needs a
 * secure context: over plain HTTP on the plant network the browser refuses, and
 * the panel says so rather than showing a black rectangle.
 */

const SCAN_INTERVAL_MS = 250;

export default function QrScanner({ open, onScan, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const handled = useRef(false);
  // the callers rebuild their handler every render; keeping it in a ref is what
  // stops the effect below tearing the camera down and reopening it each time
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const [error, setError] = useState('');

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) {
      stop();
      return undefined;
    }
    handled.current = false;
    setError('');

    let timer;
    let detector;
    let cancelled = false;

    const found = (value) => {
      if (handled.current || !value) return;
      handled.current = true;
      stop();
      onScanRef.current(value);
    };

    const tick = async () => {
      const video = videoRef.current;
      if (cancelled || !video || video.readyState !== video.HAVE_ENOUGH_DATA) return;

      if (detector) {
        try {
          const codes = await detector.detect(video);
          if (codes?.length) found(codes[0].rawValue);
          return;
        } catch {
          detector = null;   // fall through to the JS decoder for good
        }
      }

      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(frame.data, frame.width, frame.height, { inversionAttempts: 'dontInvert' });
      if (code?.data) found(code.data);
    };

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError('This browser will not open the camera. Type the job card number below instead.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        if (typeof window.BarcodeDetector === 'function') {
          try {
            detector = new window.BarcodeDetector({ formats: ['qr_code'] });
          } catch { detector = null; }
        }
        timer = setInterval(tick, SCAN_INTERVAL_MS);
      } catch (e) {
        setError(
          e?.name === 'NotAllowedError'
            ? 'Camera permission was refused. Allow it in the browser, or type the job card number below.'
            : 'The camera could not be opened. Type the job card number below instead.',
        );
      }
    })();

    return () => {
      cancelled = true;
      clearInterval(timer);
      stop();
    };
  }, [open, stop]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black" data-testid="qr-scanner">
      <div className="flex justify-end p-4">
        <button
          type="button"
          data-testid="close-camera-btn"
          onClick={() => { stop(); onClose(); }}
          aria-label="Close the scanner"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-black/60"
        >
          <X className="h-6 w-6 text-white" />
        </button>
      </div>

      <div className="relative flex flex-1 items-center justify-center overflow-hidden">
        <video ref={videoRef} playsInline muted className="h-full w-full object-cover" />
        <canvas ref={canvasRef} className="hidden" />
        {!error && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div
              className="h-56 w-56 rounded-2xl border-4"
              style={{ borderColor: Colors.accent.light }}
            />
          </div>
        )}
        {!!error && (
          <p className="absolute inset-x-6 bottom-16 text-center text-[14px] font-medium leading-6 text-white">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
